import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { parse, format } from 'date-fns';
import { qapi } from './v1_adapter';

const DEADLINE_LABELS = { '24hrs': '24 hours', '72hrs': '72 hours', '7days': '7 days' };

function SectionHeader({ icon, title }) {
  return (
    <h5 className="fw-semibold mb-3">
      <i className={`fa fa-${icon} pe-2`} />
      {title}
    </h5>
  );
}

SectionHeader.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

function ReviewPage(props) {
  const {
    backToEditing,
    publishNow,
    title,
    details,
    lookingFor,
    speakerRequest,
    manOnTheStreet,
    distribution,
    eventType,
    mediaOutletId,
    deadline,
    customDeadlineDateTime,
    schedule,
    customScheduleDateTime,
  } = props;

  const [outletName, setOutletName] = useState(mediaOutletId ? 'Loading...' : null);

  useEffect(() => {
    if (!mediaOutletId) {
      setOutletName(null);
      return;
    }
    setOutletName('Loading...');
    qapi.mediaOutlets
      .getAll({ id: mediaOutletId })
      .then((results) => setOutletName(results?.[0]?.label ?? String(mediaOutletId)))
      .catch(() => setOutletName(String(mediaOutletId)));
  }, [mediaOutletId]);

  const requestTypeLabel = () => {
    const type = lookingFor === 'product' ? 'Product' : 'Expert';
    if (speakerRequest) return `${type} (Speaker)`;
    if (manOnTheStreet) return `${type} (Person on the Street / Vox Pop)`;
    return type;
  };

  const deadlineLabel = () => {
    if (deadline === 'custom' && customDeadlineDateTime) {
      try {
        const parsed = parse(customDeadlineDateTime, "yyyy-MM-dd'T'HH:mm", new Date());
        return format(parsed, "MMMM d, yyyy 'at' h:mm a");
      } catch {
        return customDeadlineDateTime;
      }
    } else {
      return DEADLINE_LABELS[deadline] ?? deadline;
    }
  };

  const postImmediately = schedule !== 'custom' || !customScheduleDateTime;

  const scheduleLabel = () => {
    if (postImmediately) return 'Post immediately';
    try {
      const parsed = parse(customScheduleDateTime, "yyyy-MM-dd'T'HH:mm", new Date());
      return `Scheduled for ${format(parsed, "MMMM d, yyyy 'at' h:mm a")}`;
    } catch {
      return `Scheduled for ${customScheduleDateTime}`;
    }
  };

  return (
    <div className="new-request-body">
      <div className="mx-5 my-4">
        <div className="shadow-sm card px-5 pt-5 pb-4">
          <div className="text-center mb-5">
            <div className="mb-4">
              <i className="fa-solid fa-clipboard-check text-primary" style={{ fontSize: '56px' }} />
            </div>
            <h2 className="fw-semibold mb-3">Ready to post?</h2>
            <p className="text-muted lead mb-0">Take a moment to review your request, then hit Post Now.</p>
          </div>

          <div className="card p-4 mb-3">
            <SectionHeader icon="tag" title="Request Type" />
            <p className="mb-0" style={{ fontSize: '16px' }}>
              {requestTypeLabel()}
            </p>
          </div>

          <div className="card p-4 mb-3">
            <SectionHeader icon="align-left" title="Title" />
            <p className="mb-0" style={{ fontSize: '16px' }}>
              {title}
            </p>
          </div>

          <div className="card p-4 mb-3">
            <SectionHeader icon="file-lines" title="Description" />
            <p className="mb-0" style={{ fontSize: '16px', whiteSpace: 'pre-wrap' }}>
              {details}
            </p>
          </div>

          {(mediaOutletId || distribution) && (
            <div className="card p-4 mb-3">
              <SectionHeader icon="newspaper" title="Media & Distribution" />
              {mediaOutletId && (
                <div className="mb-2">
                  <span className="text-muted small">Media outlet:</span>
                  <p className="mb-0" style={{ fontSize: '16px' }}>
                    {outletName}
                  </p>
                </div>
              )}
              {distribution && (
                <div>
                  <span className="text-muted small">Distribution type:</span>
                  <p className="mb-0" style={{ fontSize: '16px' }}>
                    {distribution.charAt(0).toUpperCase() + distribution.slice(1)}
                    {eventType && ` (${eventType})`}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="card p-4 mb-3">
            <SectionHeader icon="clock" title="Deadline & Schedule" />
            <div className="mb-2">
              <span className="text-muted small">Stop accepting pitches in:</span>
              <p className="mb-0" style={{ fontSize: '16px' }}>
                {deadlineLabel()}
              </p>
            </div>
            <div>
              <span className="text-muted small">Schedule:</span>
              <p className="mb-0" style={{ fontSize: '16px' }}>
                {scheduleLabel()}
              </p>
            </div>
          </div>

          <div className="row justify-content-center gap-3 mt-5">
            <div className="col-auto">
              <button className="btn btn-outline-secondary btn-lg" type="button" onClick={backToEditing}>
                <i className="fa-solid fa-arrow-left me-2" />
                Edit
              </button>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary btn-lg" type="button" onClick={publishNow}>
                <i className="fa-solid fa-paper-plane me-2" />
                {postImmediately ? 'Post Now' : 'Schedule Post'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

ReviewPage.propTypes = {
  backToEditing: PropTypes.func.isRequired,
  customDeadlineDateTime: PropTypes.string,
  customScheduleDateTime: PropTypes.string,
  deadline: PropTypes.string.isRequired,
  details: PropTypes.string.isRequired,
  distribution: PropTypes.string,
  eventType: PropTypes.string,
  lookingFor: PropTypes.string.isRequired,
  manOnTheStreet: PropTypes.bool,
  publishNow: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  schedule: PropTypes.string.isRequired,
  speakerRequest: PropTypes.bool,
  mediaOutletId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ReviewPage.defaultProps = {
  customDeadlineDateTime: null,
  customScheduleDateTime: null,
  distribution: null,
  eventType: null,
  manOnTheStreet: false,
  speakerRequest: false,
  mediaOutletId: null,
};

export default memo(ReviewPage);
